/**
 * YMMEntryList Component
 * 
 * Manages a list of YMM (Year/Make/Model) restriction entries.
 * Allows adding and removing YMM restrictions for product fitment data.
 * Supports individual retention for each entry.
 * 
 * Requirements: 2.1, 2.2, 2.4
 */

import React from 'react';
import type { YMMRestriction } from '../types/product';
import { YMMEntryRow } from './YMMEntryRow';
import { createEmptyYMMRestriction } from '../utils/ymm';

export interface YMMWithRetention extends YMMRestriction {
  retain?: boolean;
}

interface YMMEntryListProps {
  restrictions: YMMWithRetention[];
  onChange: (restrictions: YMMWithRetention[]) => void;
}

export const YMMEntryList: React.FC<YMMEntryListProps> = ({
  restrictions,
  onChange,
}) => {
  const handleAddEntry = () => {
    const newRestriction: YMMWithRetention = { ...createEmptyYMMRestriction(), retain: false };
    onChange([...restrictions, newRestriction]);
  };

  const handleRemoveEntry = (index: number) => {
    const newRestrictions = restrictions.filter((_, i) => i !== index);
    onChange(newRestrictions);
  };

  const handleEntryChange = (
    index: number,
    field: keyof YMMRestriction,
    value: string | number
  ) => {
    const newRestrictions = restrictions.map((restriction, i) => {
      if (i === index) {
        return { ...restriction, [field]: value };
      }
      return restriction;
    });
    onChange(newRestrictions);
  };

  const handleRetentionToggle = (index: number) => {
    const newRestrictions = restrictions.map((restriction, i) => {
      if (i === index) {
        return { ...restriction, retain: !restriction.retain };
      }
      return restriction;
    });
    onChange(newRestrictions);
  };

  return (
    <div className="ymm-entry-list" data-testid="ymm-entry-list">
      <div className="ymm-header">
        <h3>Vehicle Fitment (YMM)</h3>
        <button
          type="button"
          className="ymm-add-btn"
          onClick={handleAddEntry}
          data-testid="ymm-add-btn"
        >
          + Add Fitment
        </button>
      </div>
      
      {restrictions.length === 0 ? (
        <p className="ymm-empty-message">
          No vehicle fitment restrictions. Click "Add Fitment" to add one.
        </p>
      ) : (
        <div className="ymm-entries">
          {restrictions.map((restriction, index) => (
            <YMMEntryRow
              key={index}
              restriction={restriction}
              index={index}
              onChange={handleEntryChange}
              onRemove={handleRemoveEntry}
              retain={restriction.retain}
              onRetainToggle={() => handleRetentionToggle(index)}
            />
          ))}
        </div>
      )}
    </div>
  );
};
